import React, { useCallback, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { formatLocalDateUTC } from '@/utils/date'
import { TransactionStatus } from '@/utils/transaction/status.index'
import { GridColumn as Column, Grid } from '@progress/kendo-react-grid'
import { GroupReferenceType } from 'api/Common/Types/GroupReferenceDto'
import { PageData } from 'api/Pagination/Types'
import {
  BulkWithdrawalNormalizedDto,
  BulkWithdrawalsOverviewDto,
  BulkWithdrawalsOverviewKey,
} from 'api/Wallet/Types/BulkWithdrawalsOverviewDto'
import { Pager } from 'components/components/Pager'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { links } from 'utils/featureToggles'
import { buildQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { transactionBulkTransactionWithdrawalsHistoryColumn } from './transactionBulkTransactionWithdrawalsHistoryColumn'

function normalizeBulkTransaction(
  data: BulkWithdrawalsOverviewDto[]
): BulkWithdrawalNormalizedDto[] {
  return data.map((d) => {
    return {
      [BulkWithdrawalsOverviewKey.allTransactions]: d.transactions.length,
      [BulkWithdrawalsOverviewKey.completedTransactions]: d.transactions.filter(
        (x) => x.state.id === TransactionStatus.Completed
      ).length,
      [BulkWithdrawalsOverviewKey.declinedTransactions]: d.transactions.filter(
        (x) => x.state.id === TransactionStatus.Declined
      ).length,
      [BulkWithdrawalsOverviewKey.failedTransactions]: d.transactions.filter(
        (x) => x.state.id === TransactionStatus.Failed
      ).length,
      [BulkWithdrawalsOverviewKey.name]: d.name,
      dateCreated: formatLocalDateUTC(d.dateCreated, true) || '',
      dateUpdated: formatLocalDateUTC(d.dateUpdated, true) || '',
    }
  })
}
const TransactionBulkTransactionWithdrawalsOverview = (): JSX.Element => {
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const walletsApiClient = useApiClient(WalletsApiClient)

  const [transactions, setTransactions] = useState<BulkWithdrawalNormalizedDto[]>([])

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState,
          filter?: CompositeFilterDescriptor
        ): Promise<PageData<BulkWithdrawalsOverviewDto[]>> => {
          const gridFilter = filter ?? gridState.filter
          const urlParams = new URLSearchParams(window.location.search)
          const getParam = (param: 'id') => {
            return urlParams.get(param)
          }
          const id = getParam('id')
          let query
          if (!id) {
            query = buildQuery({
              filters: [
                {
                  field: 'GroupReferenceTypeId',
                  operator: '==',
                  value: GroupReferenceType.WithdrawBulk,
                },
              ],
              logic: gridFilter.logic,
            })
          } else {
            query =
              buildQuery({
                filters: [
                  {
                    field: 'Id',
                    operator: '==',
                    value: id,
                  },
                ],
                logic: gridFilter.logic,
              }) ?? null
          }

          const response = await walletsApiClient.getBulkWithdrawalsTransactions({
            search: query,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })
          if (response?.items) {
            setTransactions(normalizeBulkTransaction(response.items))
          }

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (
      gridFilterState: GridFilterActionState
    ) => Promise<PageData<BulkWithdrawalsOverviewDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const gridFilter = filter ?? gridState.filter
      return buildQuery(gridFilter) ?? null
    },
    [gridState.filter]
  )

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()

    const response = await walletsApiClient.getBulkWithdrawalsTransactions({
      search: searchQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setTransactions(normalizeBulkTransaction(response.items))
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()

    const response = await walletsApiClient.getBulkWithdrawalsTransactions({
      search: searchQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setTransactions(normalizeBulkTransaction(response.items))
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }
  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await walletsApiClient.getBulkWithdrawalsTransactions({
      search: searchQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setTransactions(normalizeBulkTransaction(response.items))
    }
    setTableData({ ...tableData, meta: response })
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      <Breadcrumbs
        crumbs={[
          {
            title: 'Bulk Transactions - Bulk Withdrawals',
            link: links.transactionBulkWithdrawals.url,
          },
        ]}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport
          columns={transactionBulkTransactionWithdrawalsHistoryColumn}
          data={transactions}
        >
          <Grid
            {...gridState}
            resizable
            data={transactions}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
          >
            {transactionBulkTransactionWithdrawalsHistoryColumn
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

export default TransactionBulkTransactionWithdrawalsOverview
